import React, { useMemo, useCallback } from 'react';
import { Parameter } from '@shared/types';
import type { ProjectFile } from '@/components/files/FileManager';

interface SvgGroupInputProps {
  param: Parameter;
  projectFiles: ProjectFile[];
  onChange: (value: Parameter['value']) => void;
}

/**
 * Strip hardcoded fill/stroke color attributes and inline style color properties
 * from an SVG string, so the SVG renders using CSS `currentColor` which adapts
 * to the light/dark theme via the `text-foreground` wrapper class.
 *
 * Keeps all structural attributes (id, d, transform, viewBox, class, etc.).
 */
function stripSvgColors(svgString: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');

  const parserError = doc.querySelector('parsererror');
  if (parserError) return svgString; // Return as-is if unparseable

  const svgEl = doc.querySelector('svg');
  if (!svgEl) return svgString;

  // Walk all elements and strip color-related attributes/styles
  const allElements = svgEl.querySelectorAll('*');
  const colorProps = new Set([
    'fill',
    'stroke',
    'fill-opacity',
    'stroke-opacity',
    'stop-color',
    'flood-color',
    'lighting-color',
  ]);

  // Process the SVG root element itself too
  const elements = [svgEl, ...Array.from(allElements)];

  for (const el of elements) {
    // Remove color presentation attributes
    for (const prop of colorProps) {
      if (el.hasAttribute(prop)) {
        // Keep fill="none" so transparent areas stay transparent
        if (prop === 'fill' && el.getAttribute('fill') === 'none') continue;
        el.removeAttribute(prop);
      }
    }

    // Strip color-related inline style properties
    const styleAttr = el.getAttribute('style');
    if (styleAttr) {
      const filteredStyle = styleAttr
        .split(';')
        .map((s) => s.trim())
        .filter((s) => {
          if (!s) return false;
          const propName = s.split(':')[0].trim().toLowerCase();
          // Remove fill, stroke and color inline style properties
          return !colorProps.has(propName) && propName !== 'color';
        })
        .join('; ');

      if (filteredStyle) {
        el.setAttribute('style', filteredStyle);
      } else {
        el.removeAttribute('style');
      }
    }
  }

  // Ensure the SVG uses currentColor for fill
  // Inject a minimal style element so all paths inherit currentColor
  const styleEl = doc.createElementNS('http://www.w3.org/2000/svg', 'style');
  styleEl.textContent = '* { fill: currentColor; stroke: none; }';

  // Insert style as first child of svg
  if (svgEl.firstChild) {
    svgEl.insertBefore(styleEl, svgEl.firstChild);
  } else {
    svgEl.appendChild(styleEl);
  }

  // Remove width/height attrs so width:100% CSS works
  svgEl.removeAttribute('width');
  svgEl.removeAttribute('height');

  return svgEl.outerHTML;
}

/**
 * Walk up from an element to find the nearest ancestor (or self) whose id
 * is in the provided set of option IDs. Returns null if none found.
 */
function findOptionId(
  target: EventTarget | null,
  svgRoot: SVGSVGElement | null,
  optionIds: Set<string>,
): string | null {
  if (!target || !svgRoot) return null;
  let el: Element | null = target as Element;
  while (el && el !== svgRoot) {
    if (el.id && optionIds.has(el.id)) {
      return el.id;
    }
    el = el.parentElement;
  }
  return null;
}

export function SvgGroupInput({
  param,
  projectFiles,
  onChange,
}: SvgGroupInputProps) {
  const isCheckbox = param.type === 'svgcheckboxgroup';
  const optionIds = useMemo(
    () => new Set((param.options ?? []).map((o) => String(o.value))),
    [param.options],
  );

  // Current enabled/selected state
  const enabledIds = useMemo((): Set<string> => {
    if (isCheckbox) {
      const val = param.value;
      if (Array.isArray(val)) return new Set(val.map(String));
      return new Set(optionIds); // fallback: all enabled
    } else {
      const val = String(param.value);
      return new Set(val ? [val] : []);
    }
  }, [isCheckbox, param.value, optionIds]);

  // Locate the SVG file content from projectFiles
  const rawSvgContent = useMemo(() => {
    if (!param.svgFile) return null;
    const file = projectFiles.find(
      (f) =>
        f.filename.toLowerCase() === param.svgFile!.toLowerCase() &&
        f.fileType === 'svg',
    );
    if (file && typeof file.content === 'string') return file.content;
    return null;
  }, [param.svgFile, projectFiles]);

  // Process SVG: strip colors, inject opacity styles per option state
  const processedSvg = useMemo(() => {
    if (!rawSvgContent) return null;

    const stripped = stripSvgColors(rawSvgContent);

    // Inject per-id opacity via a style block
    if (optionIds.size === 0) return stripped;

    const parser = new DOMParser();
    const doc = parser.parseFromString(stripped, 'image/svg+xml');
    const svgEl = doc.querySelector('svg');
    if (!svgEl) return stripped;

    // Build opacity rules for each option id
    const opacityRules = Array.from(optionIds)
      .map((id) => {
        const enabled = enabledIds.has(id);
        const safeId = CSS.escape(id);
        return `#${safeId} { opacity: ${enabled ? '1' : '0.5'}; cursor: pointer; transition: opacity 0.15s ease; }`;
      })
      .join('\n');

    // Add a hover rule for interactive ids
    const hoverRules = Array.from(optionIds)
      .map((id) => {
        const safeId = CSS.escape(id);
        return `#${safeId}:hover { opacity: ${isCheckbox ? '0.75' : '0.85'}; }`;
      })
      .join('\n');

    // Find or create the injected style element (the first <style> added by stripSvgColors)
    const existingStyle = svgEl.querySelector('style');
    if (existingStyle) {
      existingStyle.textContent =
        (existingStyle.textContent ?? '') +
        '\n' +
        opacityRules +
        '\n' +
        hoverRules;
    }

    return svgEl.outerHTML;
  }, [rawSvgContent, optionIds, enabledIds, isCheckbox]);

  const svgRef = React.useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const svgRoot = svgRef.current?.querySelector(
        'svg',
      ) as SVGSVGElement | null;
      const clickedId = findOptionId(e.target, svgRoot, optionIds);
      if (!clickedId) return;

      if (isCheckbox) {
        const current = Array.isArray(param.value)
          ? (param.value as string[])
          : Array.from(optionIds);
        const isEnabled = current.includes(clickedId);
        const next = isEnabled
          ? current.filter((id) => id !== clickedId)
          : [...current, clickedId];
        onChange(next);
      } else {
        // Radio: clicking current selection is a no-op
        if (String(param.value) !== clickedId) {
          onChange(clickedId);
        }
      }
    },
    [isCheckbox, optionIds, param.value, onChange],
  );

  if (!processedSvg) {
    // Placeholder when SVG file is not yet uploaded
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-dashed border-border py-6 text-xs text-muted-foreground">
        {param.svgFile
          ? `SVG not found: ${param.svgFile}`
          : 'No SVG file configured'}
      </div>
    );
  }

  return (
    <div
      ref={svgRef}
      className="w-full text-foreground"
      // biome-ignore lint: SVG content is processed from project files
      dangerouslySetInnerHTML={{ __html: processedSvg }}
      onClick={handleClick}
      style={{ lineHeight: 0 }}
    />
  );
}
